import React from "react"
import { Button, Row, Col, Spin } from "antd"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { Text } from "ui/Typography"
import { ExperienceCard } from "ui/ExperienceCard"
import { PlusOutlined } from "@ant-design/icons"
import experiencesSVG from "assets/imagesV2/experiences.svg"
import styles from "./ExperiencesHome.module.css"
import { EXPERIENCE_STATES } from "constants/experiences"
import { useExperiencesActions } from "../../../../hooks/useExperiencesActions"
import { getEmptyStateTranslations } from "utils/translations"
import CustomPagination from "components/common/CustomPagination"
import { useSelector } from "react-redux"
import { VERIFICATION_STATUS } from "layoutsV2/pro/PersonalInfoLayout/constants"
import useActiveGuide from "hooks/useActiveClient"
import { BookingNotFound } from "ui/Icon/DSIcon/BookingNotFound"

const EmptyState = ({ onAddNewExperience, type }) => {
  const { t } = useTranslation()
  const translationKey = getEmptyStateTranslations(type)
  const { guide } = useSelector((state) => state.user)
  const status = guide?.verification_status
  const isPending = status === VERIFICATION_STATUS.PENDING
  const [active] = useActiveGuide()

  return (
    <div className={styles.emptyState}>
      <div className={styles.imgWrapper}>
        <BookingNotFound adventureMode={guide?.adventure_mode} />
      </div>
      <Text className="mb-s" variant="sub" size="lg">
        {t(translationKey.info)}
      </Text>
      <Text className="mb-xxl" variant="body">
        {t(translationKey.subtitle)}
      </Text>
      {type === EXPERIENCE_STATES.published && (
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={active && !isPending ? onAddNewExperience : undefined}
          disabled={!active || isPending}
        >
          {t("experiences.empty_state.add")}
        </Button>
      )}
    </div>
  )
}

export const TabContent = ({
  trips,
  onAddNewExperience,
  type,
  onActionComplete,
  metadata,
  pageSize,
  setPage,
  setPageSize,
  loading,
  showPagination,
  showSuggestions
}) => {
  const { handleDelete, handleDuplicate, handleDeactivate, handleShare, handlePublish, modal } = useExperiencesActions()
  const uiType = type === "unpublished" ? "deactivated" : type

  if (loading && !showSuggestions) {
    return (
      <div className={styles.centeredLoader}>
        <Spin size="large" />
      </div>
    )
  }

  if (trips.length === 0) {
    return <EmptyState onAddNewExperience={onAddNewExperience} type={uiType} />
  }
  const renderGrid = () => (
    <Row gutter={[20, 20]}>
      {trips.map((trip) => (
        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={12} key={trip.id}>
          <ExperienceCard
            trip={trip}
            uiType={uiType}
            onDelete={() => handleDelete(trip, onActionComplete)}
            onDeactivate={() => handleDeactivate(trip, onActionComplete)}
            onDuplicate={() => handleDuplicate(trip, onActionComplete)}
            onShare={() => handleShare(trip)}
            onPublish={() => handlePublish(trip, onActionComplete)}
          />
        </Col>
      ))}
    </Row>
  )

  const allPageSizeOptions = [10, 20, 30, 50, 100, 200, 500, 1000]
  let pageSizeOptions = allPageSizeOptions.filter((opt) => opt < metadata.total_results)
  const nextOption = allPageSizeOptions.find((opt) => opt >= metadata.total_results)
  if (nextOption) pageSizeOptions.push(nextOption)
  return (
    <>
      {renderGrid()}
      {showPagination && metadata?.total_results > 10 && (
        <div className={styles.paginationContainer}>
          {
            <CustomPagination
              metadata={metadata}
              pageSize={pageSize}
              total={metadata.total_results}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={pageSizeOptions}
            />
          }
        </div>
      )}
      {modal}
    </>
  )
}

EmptyState.propTypes = {
  onAddNewExperience: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["draft", "published", "deactivated", "unpublished"]).isRequired
}

TabContent.propTypes = {
  trips: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAddNewExperience: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["draft", "published", "deactivated", "unpublished"]).isRequired,
  onActionComplete: PropTypes.func.isRequired,
  metadata: PropTypes.shape({
    total_results: PropTypes.number
  }),
  pageSize: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  showSuggestions: PropTypes.bool.isRequired,
  showPagination: PropTypes.bool.isRequired
}
